/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Role-Based Access Control (RBAC) Permissions Hook (Task 10.1.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useMemo } from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useAuthStore } from '../stores/useAuthStore';
import type { WorkspaceMemberRole } from '../stores/useTeamStore';

export interface WorkspacePermissions {
  role: WorkspaceMemberRole;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isViewer: boolean;
  canManageMembers: boolean;
  canManageBilling: boolean;
  canDeleteWorkspace: boolean;
  canCreateApiKeys: boolean;
  canEditSettings: boolean;
  isReadOnly: boolean;
}

export function resolvePermissions(rawRole?: string | null): WorkspacePermissions {
  const normalized = (rawRole || 'member').toLowerCase() as WorkspaceMemberRole;

  const isOwner = normalized === 'owner';
  const isAdmin = normalized === 'admin' || isOwner;
  const isMember = normalized === 'member';
  const isViewer = normalized === 'viewer';

  return {
    role: normalized,
    isOwner,
    isAdmin,
    isMember,
    isViewer,
    canManageMembers: isOwner || isAdmin,
    canManageBilling: isOwner || isAdmin,
    canDeleteWorkspace: isOwner,
    canCreateApiKeys: isOwner || isAdmin,
    canEditSettings: isOwner || isAdmin,
    isReadOnly: isViewer,
  };
}

export function useWorkspacePermissions(): WorkspacePermissions {
  const { getActiveWorkspace } = useWorkspaceStore();
  const { user } = useAuthStore();

  const activeWorkspace = getActiveWorkspace();

  return useMemo(() => {
    // If active workspace has a role attached to the user summary, use that
    const roleString = activeWorkspace?.role || user?.role || 'member';
    return resolvePermissions(roleString);
  }, [activeWorkspace?.role, user?.role]);
}
